import { useMutation, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { errorFromResponse } from '@/lib/errors';
import { idempotencyKeyFor } from '@/lib/http-core';

/**
 * QA-520(order #111) 상품별 부분취소 — 결제 완료 후 출고 전 주문의 고른 상품만.
 *   POST /api/admin/v1/orders/{orderId}/item-cancel  → CANCEL 클레임 생성(approve=true 면 바로 승인 → 환불 리스트)
 *   POST /api/admin/v1/orders/{orderId}/item-status  → PG 없이 고른 상품을 환불완료로(무통장 계좌 환불 뒤)
 * 진행 중 클레임이 있으면 409, 남은 수량을 넘으면 400 — BE 메시지를 그대로 보여 준다.
 */
export type ItemCancelReason = 'CHANGE_OF_MIND' | 'WRONG_OPTION' | 'DELIVERY_DELAY' | 'FOUND_BETTER_PRICE' | 'DUPLICATE_ORDER' | 'OTHER';

export const ITEM_CANCEL_REASONS: ReadonlyArray<[ItemCancelReason, string]> = [
  ['CHANGE_OF_MIND', '단순 변심'],
  ['WRONG_OPTION', '옵션 잘못 선택'],
  ['DELIVERY_DELAY', '배송 지연'],
  ['FOUND_BETTER_PRICE', '더 저렴한 곳 발견'],
  ['DUPLICATE_ORDER', '중복 주문'],
  ['OTHER', '기타 (품절 등 — 메모에 적기)'],
];

export interface ItemSelection {
  orderItemId: string;
  /** 비우면(null) 그 상품의 남은 수량 전부. */
  quantity: number | null;
}

export interface ItemCancelResult {
  orderId: string;
  claimId: string | null;
  claimStatus: string | null;
  orderStatus: string | null;
}

const post = async (path: string, body: unknown): Promise<ItemCancelResult> => {
  const res = await adminFetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Idempotency-Key': idempotencyKeyFor('POST', path, { ...(body as object), at: Date.now() }) },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await errorFromResponse(res);
  const env = (await res.json()) as { data?: Partial<ItemCancelResult> | null };
  return {
    orderId: env.data?.orderId ?? '',
    claimId: env.data?.claimId ?? null,
    claimStatus: env.data?.claimStatus ?? null,
    orderStatus: env.data?.orderStatus ?? null,
  };
};

const items = (sel: ReadonlyArray<ItemSelection>) =>
  sel.map((s) => (s.quantity == null ? { orderItemId: s.orderItemId } : { orderItemId: s.orderItemId, quantity: s.quantity }));

const useInvalidate = () => {
  const qc = useQueryClient();
  return async () => {
    await qc.invalidateQueries({ queryKey: ['admin', 'order'] });
    await qc.invalidateQueries({ queryKey: ['admin', 'orders'] });
    await qc.invalidateQueries({ queryKey: ['admin', 'claims'] });
  };
};

export const useItemCancelMutation = () => {
  const invalidate = useInvalidate();
  return useMutation<ItemCancelResult, Error, { orderId: string; selection: ItemSelection[]; reasonCode: ItemCancelReason; note: string; approve: boolean }>({
    mutationFn: ({ orderId, selection, reasonCode, note, approve }) =>
      post(`/api/admin/v1/orders/${orderId}/item-cancel`, { items: items(selection), reasonCode, note: note.trim() || null, approve }),
    onSuccess: invalidate,
  });
};

export const useItemRefundDoneMutation = () => {
  const invalidate = useInvalidate();
  return useMutation<
    ItemCancelResult,
    Error,
    { orderId: string; selection: ItemSelection[]; reasonCode: ItemCancelReason; note: string; refundedAmount: number | null }
  >({
    mutationFn: ({ orderId, selection, reasonCode, note, refundedAmount }) =>
      post(`/api/admin/v1/orders/${orderId}/item-status`, {
        items: items(selection),
        targetStatus: 'REFUND_DONE',
        reasonCode,
        note: note.trim(),
        ...(refundedAmount != null ? { refundedAmount } : {}),
      }),
    onSuccess: invalidate,
  });
};
